import { GenericStyleModel } from '../../Styles/models/generic-style.model';
import { StyleStateEnum } from '../enums/style-state.enum';
import { DefaultFontsEnum } from '../enums/default-fonts.enum';
import { ButtonStyleAlignmentEnum } from '../enums/button-style.enum';
import { PaddingModel } from './padding.model';

export const defaultFontFamily: string = 'Arial, Helvetica, sans-serif';
export const defaultFontColor: string = '#FFFFFF';
export const defaultButtonColor: string = '#007BD8';
export const defaultButtonBackgroundColor: string = '#FFFFFF';

export class ButtonStyleModel extends GenericStyleModel {
    declare styleState: number | string;
    declare displayStyleState: number | string;
    fontColor: string;
    fontFamily: string;
    fontSize: string;
    bold: boolean = true;
    italics: boolean = false;
    underlined: boolean = false;
    buttonColor: string;
    buttonBackgroundColor: string;
    buttonAlignment: ButtonStyleAlignmentEnum = ButtonStyleAlignmentEnum.center;
    buttonShape: number = 0;
    padding: PaddingModel = new PaddingModel();


    constructor(apiModel?: any) {
        super(apiModel);

        if (apiModel) {
            this.setFromApiModel(apiModel);
        }

        if (!this.fontColor) {
            this.fontColor = defaultFontColor;
        }
        if (!this.fontFamily) {
            this.fontFamily = defaultFontFamily;
        }
        if (!this.fontSize) {
            this.fontSize = '14px';
        }
        if (!this.buttonColor) {
            this.buttonColor = defaultButtonColor;
        }
        if (!this.buttonBackgroundColor) {
            this.buttonBackgroundColor = defaultButtonBackgroundColor;
        }
        if (!(this.buttonShape || this.buttonShape === 0)) {
            this.buttonShape = 0;
        }
        if (!this.styleState) {
            this.styleState = StyleStateEnum.DEFAULT;
        }
        if (!this.displayStyleState) {
            this.displayStyleState = this.styleState;
        }
    }

    private getButtonStyles(): any {
        return {
            fontColor: this.fontColor,
            fontFamily: this.fontFamily,
            fontSize: this.fontSize,
            bold: this.bold,
            italics: this.italics,
            underlined: this.underlined,
            buttonColor: this.buttonColor,
            buttonBackgroundColor: this.buttonBackgroundColor,
            buttonAlignment: this.buttonAlignment,
            buttonShape: this.buttonShape,
            padding: this.padding
        };
    }

    clone(): ButtonStyleModel {
        const clone = new ButtonStyleModel(
            {
                ...this.getButtonStyles(),
                styleState: this.styleState,
                displayStyleState: this.displayStyleState
            }
        );

        if (this.maxWidthPx) {
            clone.maxWidthPx = this.maxWidthPx;
        }

        if (this.template) {
            clone.template = this.template.clone();
        }

        return clone;
    }

    equals(other: ButtonStyleModel, compareTemplate: boolean): boolean {
        if (!other) {
            return false;
        }

        // If they're both saved styles we don't need to look further
        // @HACK This if condition is complicated because clone isn't used in all places, some use JSON.parse(JSON.stringify(, but it's complicated to update
        // that so for now if other doesn't have isSavedStyle, just check to see that other.styleState is a number, which is equivalent
        if (
            this.isSavedStyle()
            && (
                (other.hasOwnProperty('isSavedStyle') && other.isSavedStyle())
                || !!Number(other.styleState)
            )
        ) {
            return this.styleState === other.styleState;
        }

        if (!super.equals(other, compareTemplate)) {
            return false;
        }

        for (const prop in this.getButtonStyles()) {
            if (prop === 'bold') {
                continue;
            }

            if (this[prop]?.equals) {
                if (!this[prop].equals(other[prop])) {
                    return false;
                }
            } else {
                if (this[prop] !== other[prop]) {
                    return false;
                }
            }
        }

        if (!!this.bold !== !!other.bold) {
            return false;
        }
        if (this.styleState !== other.styleState) {
            return false;
        }
        if (this.displayStyleState !== other.displayStyleState) {
            return false;
        }

        if (!this.padding.equals(other.padding)){
            return false;
        }

        return true;
    }

    setFromApiModel(apiModel: any): void {
        super.setFromApiModel(apiModel);

        if (typeof apiModel === 'string') {
            apiModel = JSON.parse(apiModel);
        }

        for (const prop in this.getButtonStyles()) {
            if (prop === 'fontFamily') {
                // Custom check below
                continue;
            }
            if (apiModel.hasOwnProperty(prop)) {
                this[prop] = apiModel[prop];
            }
        }

        if (this.fontFamily === DefaultFontsEnum.backwardCompatibilityArial) {
            this.fontFamily = DefaultFontsEnum.Arial;
        } else if (this.fontFamily === DefaultFontsEnum.backwardCompatibilityHelvetica) {
            this.fontFamily = DefaultFontsEnum.Helvetica;
        }

        if (apiModel.hasOwnProperty('styleState')) {
            this.styleState = apiModel.styleState;
        }
        if (apiModel.hasOwnProperty('displayStyleState')) {
            this.displayStyleState = apiModel.displayStyleState;
        }

        if (apiModel.hasOwnProperty('padding')) {
            this.padding = new PaddingModel(apiModel.padding.paddingAll, apiModel.padding.paddingOption, apiModel.padding.topPadding,
                apiModel.padding.leftPadding, apiModel.padding.bottomPadding, apiModel.padding.rightPadding);
        }
    }

    toApi(): any {
        return {
            ...this.getButtonStyles(),
            defaultReference: this.defaultReference ? this.defaultReference : StyleStateEnum.DEFAULT
        };
    }

    toBlockStyle(): any {
        if (!this.isSavedStyle()) {
            return {
                ...this.getButtonStyles(),
                styleState: this.styleState,
                displayStyleState: this.displayStyleState,
            };
        } else {
            return {
                styleState: this.styleState
            };
        }
    }
}
